/**
 * Crossroads content: field definitions shared by the editor form, CSV import/export and
 * client-side validation. Field keys match the backend (controllers/questions.js).
 */

export const QUIZ_MAX_QUESTIONS = 100;
export const QUIZ_PAGE_SIZES = [10, 25, 50, 100];
const HEX_RE = /^#[0-9a-f]{6}$/i;

/** [key, CSV header, label, max length] — in CSV column order. */
export const CROSSROADS_FIELDS = [
  ["question_name", "scenario", "Scenario description", 300],
  ["option_one", "option_a", "Option A", 150],
  ["option_two", "option_b", "Option B", 150],
  ["correct_answer", "correct_answer", "Correct answer", 150],
  ["dyk_heading", "dyk_heading", "“Did You Know?” heading", 100],
  ["more_info", "dyk_content", "Learning content", 200],
  ["points", "points", "Points", 4],
  ["path_color_mode", "path_color_mode", "Path colour mode", 10],
  ["path_a_color", "path_a_color", "Path A colour", 7],
  ["path_b_color", "path_b_color", "Path B colour", 7],
];
export const FIELD_MAX = Object.fromEntries(CROSSROADS_FIELDS.map(([k, , , max]) => [k, max]));
export const QUIZ_CSV_HEADERS = CROSSROADS_FIELDS.map(([, header]) => header);

const HEADER_ALIASES = {
  ...Object.fromEntries(CROSSROADS_FIELDS.map(([key, header]) => [header, key])),
  scenario_description: "question_name",
  question_name: "question_name",
  question: "question_name",
  option_one: "option_one",
  option1: "option_one",
  option_1: "option_one",
  option_two: "option_two",
  option2: "option_two",
  option_2: "option_two",
  answer: "correct_answer",
  correct: "correct_answer",
  more_info: "more_info",
  learning_content: "more_info",
};

/** "choice" for a crossroads with no right answer; anything else is scored. */
export function normalizeQuestionType(raw) {
  const t = String(raw ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
  return ["choice", "choice_based", "no_correct", "no_correct_answer", "opinion"].includes(t) ? "choice" : "scored";
}

export function emptyCrossroads() {
  return {
    question_name: "",
    option_one: "",
    option_two: "",
    question_type: "scored",
    correct: "",
    path_color_mode: "theme",
    path_a_color: "",
    path_b_color: "",
    dyk_heading: "",
    more_info: "",
    points: "10",
  };
}

/** Saved row → editor draft (correct answer as 'A' / 'B'). */
export function draftFromRow(row) {
  const d = emptyCrossroads();
  Object.keys(d).forEach((k) => {
    if (row?.[k] != null) d[k] = String(row[k]);
  });
  d.question_type = normalizeQuestionType(row?.question_type);
  d.correct = String(row?.answer_id) === "2" ? "B" : String(row?.answer_id) === "1" ? "A" : "";
  d.path_color_mode = ["custom", "random"].includes(row?.path_color_mode) ? row.path_color_mode : "theme";
  d.points = row?.points == null || row.points === "" ? "10" : String(row.points);
  return d;
}

/**
 * Validates an editor draft / CSV row. Returns { ok, data } where data is the payload the
 * backend expects, or { ok: false, field, message }.
 */
export function validateCrossroads(input, { rowLabel = "", hasCorrectAnswers = true } = {}) {
  const prefix = rowLabel ? `${rowLabel} — ` : "";
  const v = (k) => String(input?.[k] ?? "").trim();
  const fail = (field, message) => ({ ok: false, field, message: prefix + message });

  for (const [key, , label, max] of CROSSROADS_FIELDS) {
    if (key === "correct_answer") continue;
    if (v(key).length > max) return fail(key, `${label} has ${v(key).length} characters (max ${max}).`);
  }
  if (!v("question_name")) return fail("question_name", "Scenario description is required.");
  if (!v("option_one")) return fail("option_one", "Option A is required.");
  if (!v("option_two")) return fail("option_two", "Option B is required.");
  if (!v("more_info")) return fail("more_info", "“Did You Know?” learning content is required.");
  if (v("option_one").toLowerCase() === v("option_two").toLowerCase()) {
    return fail("option_two", "Option A and Option B must be different.");
  }

  // Game Settings decides for every crossroads: with correct answers, or all choice based.
  const type = hasCorrectAnswers ? "scored" : "choice";
  // Editor sends correct = 'A' | 'B'; CSV may send A/B or the option's text.
  let correct = type === "choice" ? "" : v("correct").toUpperCase();
  if (type === "scored" && correct !== "A" && correct !== "B") {
    const raw = v("correct_answer");
    const r = raw.toLowerCase();
    if (r === v("option_one").toLowerCase() || ["a", "1", "option a"].includes(r)) correct = "A";
    else if (r === v("option_two").toLowerCase() || ["b", "2", "option b"].includes(r)) correct = "B";
    else return fail("correct", raw ? `Correct answer "${raw}" must be A, B, or match an option exactly.` : "Choose the correct answer.");
  }

  const pointsRaw = v("points");
  if (type === "scored" && pointsRaw && (!/^\d+$/.test(pointsRaw) || Number(pointsRaw) > 1000)) {
    return fail("points", "Points must be a whole number from 0 to 1000.");
  }

  const mode = ["custom", "random"].includes(v("path_color_mode").toLowerCase()) ? v("path_color_mode").toLowerCase() : "theme";
  if (mode === "custom") {
    if (!HEX_RE.test(v("path_a_color")) || !HEX_RE.test(v("path_b_color"))) {
      return fail("path_a_color", "Custom path colours must be hex colours like #2E86DE.");
    }
    if (v("path_a_color").toLowerCase() === v("path_b_color").toLowerCase()) {
      return fail("path_b_color", "Path A and Path B colours must be different.");
    }
  }

  return {
    ok: true,
    data: {
      question_name: v("question_name"),
      option_one: v("option_one"),
      option_two: v("option_two"),
      question_type: type,
      correct_answer: type === "choice" ? "" : correct === "A" ? v("option_one") : v("option_two"),
      path_color_mode: mode,
      path_a_color: mode === "custom" ? v("path_a_color") : "",
      path_b_color: mode === "custom" ? v("path_b_color") : "",
      dyk_heading: v("dyk_heading"),
      more_info: v("more_info"),
      points: type === "choice" ? "0" : pointsRaw === "" ? "10" : String(Number(pointsRaw)),
    },
  };
}

// ---- CSV ----

const SAMPLE_ROWS = [
  ["An email from \"IT Support\" asks you to confirm your password through a link before the end of the day.","Click the link and confirm quickly","Report it to the security team","B","Did You Know?","Most cyber attacks start with a phishing email.","10","theme","",""],
  ["You realise your part of a team project will be two days late.","Tell your manager now and propose a new plan","Stay quiet and hope nobody notices","A","Did You Know?","Raising risks early gives everyone the most options to fix them.","10","random","",""],
  ["A supplier offers you expensive concert tickets just before a contract renewal.","Accept — it's just a friendly gesture","Politely decline and log the offer","B","Did You Know?","Gifts during active business decisions can create a real or perceived conflict of interest.","10","theme","",""],
  ["You notice a liquid spill near the office staircase.","Mark it and report it to facilities","Step around it — someone else will handle it","A","Did You Know?","Slips, trips and falls are among the most common workplace injuries.","10","custom","#27AE60","#C0392B"],
  ["An upset customer emails about a delayed order, and it's not your department.","Forward it without a note","Acknowledge them and connect them to the right person","B","Did You Know?","A fast acknowledgement can matter more to customers than a fast fix.","10","theme","",""],
  ["You're about to share your screen in a client call with confidential files open.","Close other windows and share one app only","Share the whole desktop to save time","A","Did You Know?","Sharing a single window instead of your desktop prevents accidental data leaks.","10","random","",""],
  ["A new colleague seems lost in their first week and hasn't asked for help.","Offer to walk them through the team's tools","Leave them — they'll figure it out","A","Did You Know?","People with a buddy in their first weeks ramp up noticeably faster.","10","theme","",""],
  ["A process step feels pointless and would save an hour if skipped.","Skip it this once","Raise it with the process owner to improve it","B","Did You Know?","Many \"pointless\" steps exist because of a past incident.","10","theme","",""],
  ["A teammate's presentation had a factual error in front of leadership.","Point it out loudly in the meeting","Share it with them privately afterwards","B","Did You Know?","Feedback lands best when it's specific, timely and private.","10","custom","#8E44AD","#16A085"],
  ["You find an employee ID badge on the floor near the entrance.","Hand it to security or reception","Keep it on your desk until someone asks","A","Did You Know?","A lost badge is an open door until it's reported.","10","theme","",""],
];

function escapeCsv(val) {
  const s = String(val ?? "");
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(lines, filename) {
  // BOM so Excel opens UTF-8 (quotes, emoji, accents) correctly.
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function downloadSampleQuizCsv() {
  // Sample rows fill the core columns; the optional ones are left empty.
  const pad = (r) => [...r, ...Array(Math.max(0, QUIZ_CSV_HEADERS.length - r.length)).fill("")];
  downloadCsv(
    [QUIZ_CSV_HEADERS.join(","), ...SAMPLE_ROWS.map((r) => pad(r).map(escapeCsv).join(","))],
    "crossroads_sample.csv"
  );
}

export function exportQuizCsv(questions) {
  const rows = (questions || []).map((q) => {
    const d = draftFromRow(q);
    const values = {
      ...d,
      correct_answer: d.question_type === "choice" ? "" : d.correct,
      path_a_color: d.path_color_mode === "custom" ? d.path_a_color : "",
      path_b_color: d.path_color_mode === "custom" ? d.path_b_color : "",
    };
    return CROSSROADS_FIELDS.map(([key]) => escapeCsv(values[key])).join(",");
  });
  downloadCsv([QUIZ_CSV_HEADERS.join(","), ...rows], "crossroads_export.csv");
}

/** RFC-4180-ish parser: quoted fields may contain commas, quotes ("") and line breaks. */
function parseCsvText(text) {
  const rows = [];
  let row = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          cur += '"';
          i += 1;
        } else inQuotes = false;
      } else cur += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === ",") {
      row.push(cur);
      cur = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(cur);
      rows.push(row);
      row = [];
      cur = "";
    } else cur += ch;
  }
  if (cur !== "" || row.length) {
    row.push(cur);
    rows.push(row);
  }
  return rows;
}

const normalizeHeader = (h) =>
  String(h ?? "")
    .trim()
    .toLowerCase()
    .replace(/[“”"]/g, "")
    .replace(/\s+/g, "_");

export const QUIZ_CSV_HEADER_REQUIRED_MESSAGE =
  "The first row must be a header row with at least: scenario, option_a, option_b, dyk_content (plus correct_answer when crossroads have a correct answer). Download Sample CSV for the full format.";

export function parseQuizCsv(text) {
  const raw = String(text ?? "").replace(/^﻿/, "");
  if (!raw.trim()) throw new Error("The CSV file is empty.");
  const table = parseCsvText(raw).filter((r) => r.some((c) => String(c).trim() !== ""));
  if (table.length < 2) throw new Error(QUIZ_CSV_HEADER_REQUIRED_MESSAGE);

  const col = {};
  table[0].forEach((h, i) => {
    const key = HEADER_ALIASES[normalizeHeader(h)];
    if (key && col[key] === undefined) col[key] = i;
  });
  const missing = ["question_name", "option_one", "option_two"].filter((k) => col[k] === undefined);
  if (missing.length) {
    const names = { question_name: "scenario", option_one: "option_a", option_two: "option_b", correct_answer: "correct_answer" };
    throw new Error(`Missing column(s): ${missing.map((m) => names[m]).join(", ")}. ${QUIZ_CSV_HEADER_REQUIRED_MESSAGE}`);
  }

  return table.slice(1).map((cells) => {
    const row = {};
    Object.entries(col).forEach(([key, i]) => {
      row[key] = String(cells[i] ?? "").trim();
    });
    return row;
  });
}
